/**
 * Outcome model for `POST /collections/{uuid}/detect` (see `detectCollection`).
 *
 * The endpoint is two-phase: a request without `confirm` starts nothing and
 * instead reports what *would* happen. Both detection entry points — the Object
 * Picker wizard and the map's boundary editor — classify the response through
 * this module so the two flows can never drift apart.
 */

/** The three outcomes a detect request can report. */
export const DETECTION_OUTCOME = Object.freeze({
	/** A job was started; `job_uuid` identifies it. */
	STARTED: 'started',
	/** Nothing to detect — this ground is already covered for every object. */
	ALREADY_DETECTED: 'alreadyDetected',
	/** A run is needed but wasn't confirmed; repeat with `confirm: true`. */
	NEEDS_CONFIRMATION: 'needsConfirmation',
});

/**
 * Classify a detect response into one of {@link DETECTION_OUTCOME}.
 *
 * `job_uuid` is checked first and `ran` second: a started job is the only
 * outcome that carries either, so together they're the most reliable signal.
 * `requires_confirmation` then separates "must run" from "nothing to run".
 *
 * Every real outcome is identified positively, so an unrecognized shape returns
 * `null` rather than falling through to already-detected. Treating an unknown
 * response as "results are available" is not the conservative reading it looks
 * like: it sends the user a success toast and loads results for a request whose
 * fate is unknown. Callers must handle `null` as an error.
 *
 * @param {{ ran?: boolean, requires_confirmation?: boolean, job_uuid?: string|null,
 *   run_preview?: { scan_area_m2?: number }|null }|null|undefined} result
 * @returns {typeof DETECTION_OUTCOME[keyof typeof DETECTION_OUTCOME]|null} `null`
 *   when the response matches no known outcome
 */
export const classifyDetectionResult = result => {
	if (result?.job_uuid || result?.ran === true) return DETECTION_OUTCOME.STARTED;
	if (result?.requires_confirmation === true) return DETECTION_OUTCOME.NEEDS_CONFIRMATION;
	if (result?.ran === false || result?.requires_confirmation === false)
		return DETECTION_OUTCOME.ALREADY_DETECTED;
	return null;
};

/**
 * Validate a `run_preview.scan_area_m2` value for display, in square metres.
 *
 * The unit is left alone so the display layer can scale it (see `formatAoiArea`
 * in `@utils/aoiStats`): a scan area can be a few hundred square metres, which
 * no hectare figure can express as anything but zero.
 *
 * Returns `null` when there's no measurable area: `run_preview` is null whenever
 * the ground a run would scan can't be measured (e.g. an orthophoto project
 * whose extent isn't recorded yet), and callers should omit the figure rather
 * than render a misleading zero.
 *
 * A negative area is not physically meaningful, so it's treated the same way.
 * Without this, the figure reaches the UI intact and the run summary offers the
 * user "-25,000 m²" of ground to scan. Zero stays valid — it's a real
 * measurement of no new ground.
 *
 * @param {number|null|undefined} squareMetres
 * @returns {number|null} square metres, or `null` when unmeasurable or negative
 */
export const scanAreaSquareMeters = squareMetres => {
	if (typeof squareMetres !== 'number' || !Number.isFinite(squareMetres)) return null;
	if (squareMetres < 0) return null;
	return squareMetres;
};

/**
 * Pull the scan-area preview (in square metres) straight off a detect response.
 * Convenience wrapper so callers don't have to reach through `run_preview`.
 *
 * @param {{ run_preview?: { scan_area_m2?: number }|null }|null|undefined} result
 * @returns {number|null} square metres, or `null` when unmeasurable
 */
export const getScanAreaSquareMeters = result =>
	scanAreaSquareMeters(result?.run_preview?.scan_area_m2);
